import { useMemo } from "react";
import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../redux/slices/authSlice";
import {
  companiesSelector,
  currentSelector,
  getCategories,
  getCategoryItemizers,
  getCompanies,
  getFloors,
  getHouseAccounts,
  getItemizers,
  getPayments,
  getTaxes,
  setCurrentId,
} from "../../redux/slices/companiesSlice";
import {
  getItemizerCodesForToken,
  getItemizerRoute,
} from "../../utils/itemizerRules";

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 16px 12px;
`;

const Title = styled.div`
  color: #aab4c8;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 800px;
  min-height: 0;
  overflow-y: auto;
`;

const AccountButton = styled.button`
  appearance: none;
  background: ${({ selected }) => (selected ? "#5187e0" : "#343e53")};
  border: 0;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  font: inherit;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 700;
`;

const Count = styled.div`
  font-size: 11px;
  margin-top: 4px;
  opacity: 0.75;
`;

const getAccountId = (companyId = "") => String(companyId).split("-")[0];
const getCompanyName = (company) => {
  const name = company.company_name || "";
  const prefix = `${company.company_id}-`;
  return name.startsWith(prefix) ? name.slice(prefix.length) : name;
};

const SidebarAccounts = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { token } = useSelector(authSelector);
  const { companiesList } = useSelector(companiesSelector);
  const { currentId } = useSelector(currentSelector);

  const accounts = useMemo(
    () =>
      companiesList.reduce((result, company) => {
        const id = getAccountId(company.company_id);
        if (!id) return result;

        const account = result.find((item) => item.id === id);
        if (account) {
          account.companies.push(company);
        } else {
          result.push({ id, companies: [company] });
        }
        return result;
      }, []),
    [companiesList]
  );

  if (accounts.length < 2) return null;

  const currentAccountId = getAccountId(currentId) || accounts[0].id;
  const itemizerCodes = getItemizerCodesForToken({ token });
  const itemizerActions = itemizerCodes.reduce(
    (actions, code, index) => ({
      ...actions,
      [getItemizerRoute(code, index)]: () => getItemizers({ Itemizer: code }),
    }),
    { "/details/itemizers": () => getItemizers() }
  );
  const fnDict = {
    "/": () => getCompanies(),
    "/details": () => getFloors(),
    ...itemizerActions,
    "/details/category": () => getCategories(),
    "/details/payments": () => getPayments(),
    "/details/payments-cross-posting": () =>
      getPayments({ operation_id: "crossposting" }),
    "/details/tax": () => getTaxes(),
  };

  const selectAccount = (account) => {
    const firstCompany = account.companies[0];
    if (!firstCompany?.company_id || currentAccountId === account.id) return;

    dispatch(setCurrentId(firstCompany.company_id));
    if (pathname in fnDict) dispatch(fnDict[pathname]());
    if (pathname === "/details/category") dispatch(getCategoryItemizers());
    if (
      pathname === "/details/payments" ||
      pathname === "/details/payments-cross-posting"
    ) {
      dispatch(getHouseAccounts());
    }
  };

  return (
    <Wrapper>
      <Title>Account</Title>
      <List>
        {accounts.map((account) => (
          <AccountButton
            key={account.id}
            selected={currentAccountId === account.id}
            onClick={() => selectAccount(account)}
            title={getCompanyName(account.companies[0])}
            type="button"
          >
            <Name>{account.id}</Name>
            <Count>{getCompanyName(account.companies[0])}</Count>
            <Count>
              {account.companies.length} location
              {account.companies.length === 1 ? "" : "s"}
            </Count>
          </AccountButton>
        ))}
      </List>
    </Wrapper>
  );
};

export default SidebarAccounts;
